import { useState, useEffect } from 'react'
import { ArrowLeft, Search, X, UserPlus, ChevronDown, ChevronUp, Trash2, Users, RefreshCw } from 'lucide-react'
import { useGroupDetail, useUpdateGroup, useDeleteGroup, useSaveGroupMembers } from '../hooks/useGroups'
import { useQueryClient } from '@tanstack/react-query'
import { usePlayers, useCreatePlayer, useDeletePlayer, useGhinLookup, useMyPlayer } from '../hooks/usePlayers'
import { useMyFollows } from '../hooks/useFollows'
import { useAuth } from '../contexts/AuthContext'
import { getInitials } from '../utils/colors'
import { hasHandicapIndex, formatHandicapIndex } from '../utils/handicaps'
import Spinner from './Spinner'
import logger from '../utils/logger'

const AVATAR_COLORS = [
  '#0ea47a', '#5e9be6', '#f59e0b', '#ef4444',
  '#a78bfa', '#f472b6', '#fb923c', '#22d3ee',
]

export default function GroupDetail({ groupId, isNew, onBack }) {
  const { user } = useAuth()
  const { data: group, isLoading } = useGroupDetail(groupId)
  const { data: allPlayers } = usePlayers()
  const { data: myFollows } = useMyFollows()
  const updateGroup = useUpdateGroup()
  const deleteGroupMut = useDeleteGroup()
  const saveMembers = useSaveGroupMembers()
  const queryClient = useQueryClient()
  const createPlayer = useCreatePlayer()
  const { data: myPlayer } = useMyPlayer()
  const deletePlayer = useDeletePlayer()
  const ghinLookup = useGhinLookup()

  const [groupName, setGroupName] = useState('')
  const [memberPlayerIds, setMemberPlayerIds] = useState([])
  const [searchText, setSearchText] = useState('')
  const [initialized, setInitialized] = useState(false)
  const [showManualForm, setShowManualForm] = useState(false)
  const [manualName, setManualName] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  const [manualColor, setManualColor] = useState(AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)])
  const [manualGhin, setManualGhin] = useState('')
  const [manualHandicap, setManualHandicap] = useState('')
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [pressedAddId, setPressedAddId] = useState(null)

  // Initialize local state from fetched group
  useEffect(() => {
    if (group && !initialized) {
      setGroupName(group.name)
      setMemberPlayerIds((group.members || []).map((m) => m.player_id))
      setInitialized(true)
    }
  }, [group, initialized])

  if (isLoading || !group) return <Spinner message="Loading..." />

  const isOwner = group.isOwner

  // Build a map of player data for display
  const allPlayerMap = new Map()
  ;(allPlayers || []).forEach((p) => allPlayerMap.set(p.id, p))
  ;(group.members || []).forEach((m) => { if (m.player) allPlayerMap.set(m.player_id, m.player) })

  const memberPlayers = memberPlayerIds.map((id) => allPlayerMap.get(id)).filter(Boolean)

  // Search logic (same pattern as AddPlayersView)
  const memberSet = new Set(memberPlayerIds)
  const followedPlayers = myFollows?.filter((p) => !memberSet.has(p.id)) || []
  const followedUserIds = new Set((myFollows || []).map((p) => p.user_id).filter(Boolean))

  const isSearching = searchText.length >= 1

  const matchingFollowed = isSearching
    ? followedPlayers.filter((p) => {
        const q = searchText.toLowerCase()
        return p.first_name?.toLowerCase().startsWith(q) ||
               p.last_name?.toLowerCase().startsWith(q) ||
               p.email?.toLowerCase().startsWith(q) ||
               p.display_name?.toLowerCase().split(/\s+/).some(w => w.startsWith(q))
      })
    : []

  const followedPlayerIds = new Set(matchingFollowed.map((p) => p.id))

  const localResults = isSearching
    ? (allPlayers || []).filter((p) => {
        if (memberSet.has(p.id)) return false
        if (followedPlayerIds.has(p.id)) return false
        if (p.user_id && followedUserIds.has(p.user_id)) return false
        if (!p.user_id && p.created_by !== user?.id) return false
        const q = searchText.toLowerCase()
        return p.first_name?.toLowerCase().startsWith(q) ||
               p.last_name?.toLowerCase().startsWith(q) ||
               p.email?.toLowerCase().startsWith(q) ||
               p.display_name?.toLowerCase().split(/\s+/).some(w => w.startsWith(q))
      })
    : []

  // Dedup suggestions: manual players created by this user that match the typed name
  const manualDedupSuggestions = manualName.trim().length >= 2
    ? (allPlayers || []).filter((p) =>
        !p.user_id &&
        p.created_by === user?.id &&
        !memberSet.has(p.id) &&
        p.display_name?.toLowerCase().includes(manualName.trim().toLowerCase())
      )
    : []

  const handleAddPlayer = (playerId) => {
    if (!memberSet.has(playerId)) {
      setMemberPlayerIds((prev) => [...prev, playerId])
    }
  }

  const handleRemovePlayer = (playerId) => {
    setMemberPlayerIds((prev) => prev.filter((id) => id !== playerId))
  }

  const handleManualAdd = (e) => {
    e.preventDefault()
    if (!manualName.trim()) return

    createPlayer.mutate(
      {
        display_name: manualName.trim(),
        email: manualEmail.trim() || undefined,
        avatar_color: manualColor,
        ...(manualGhin.trim() ? { ghin_number: manualGhin.trim() } : {}),
        ...(manualHandicap !== '' ? { handicap_index: parseFloat(manualHandicap) } : {}),
      },
      {
        onSuccess: (newPlayer) => {
          handleAddPlayer(newPlayer.id)
          setManualName('')
          setManualEmail('')
          setManualGhin('')
          setManualHandicap('')
          setManualColor(AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)])
          setShowManualForm(false)
        },
      }
    )
  }

  const handleSave = () => {
    if (groupName.trim() && groupName !== group.name) {
      updateGroup.mutate({ groupId, name: groupName.trim() })
    }
    saveMembers.mutate({ groupId, playerIds: memberPlayerIds }, {
      onSuccess: () => onBack(),
    })
  }

  const handleBackClick = () => {
    if (isNew) {
      setShowDiscardConfirm(true)
    } else {
      onBack()
    }
  }

  const handleDiscard = () => {
    // Optimistically remove from cache so the group list doesn't flash the stale card
    queryClient.setQueryData(['my-groups'], (old) =>
      old ? old.filter((g) => g.id !== groupId) : old
    )
    deleteGroupMut.mutate(groupId, {
      onSuccess: () => { setShowDiscardConfirm(false); onBack() },
      onError: (err) => {
        logger.capture(err, { source: 'GroupDetail', action: 'deleteGroup', groupId })
        queryClient.invalidateQueries({ queryKey: ['my-groups'] })
        alert('Failed to discard. Please check your connection and try again.')
      },
    })
  }

  const handleDeleteGroup = () => {
    setShowDiscardConfirm(true)
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={handleBackClick}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-tertiary)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700', flex: 1 }}>
          {isOwner ? 'Edit Group' : 'View Group'}
        </h1>
        {isOwner && (
          <button
            onClick={() => setShowDiscardConfirm(true)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '9999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--accent-danger, #ef4444)',
              flexShrink: 0,
            }}
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>

      {/* Group name (owner only) */}
      {isOwner ? (
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
            Group Name
          </label>
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g. Myrtle Beach Crew"
            style={{
              width: '100%',
              height: '44px',
              padding: '0 0.75rem',
              borderRadius: '0.5rem',
              fontSize: '0.9375rem',
              fontWeight: '600',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      ) : (
        <div style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '700' }}>{group.name}</h2>
        </div>
      )}

      {/* Current members */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        padding: '1rem',
        marginBottom: '1rem',
      }}>
        <h3 style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '0.75rem',
        }}>
          Members ({memberPlayers.length})
        </h3>

        {memberPlayers.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center', padding: '0.5rem 0' }}>
            No members yet
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {memberPlayers.map((player) => (
              <div
                key={player.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  padding: '0.5rem 0.25rem',
                  borderBottom: '1px solid var(--border-color)',
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: player.avatar_color || '#0ea47a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '0.75rem',
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  {getInitials(player)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                    {player.first_name || player.last_name
                      ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
                      : player.display_name}
                  </span>
                  {!player.user_id && (
                    <span style={{
                      marginLeft: '0.5rem',
                      fontSize: '0.5625rem',
                      fontWeight: '600',
                      color: 'var(--text-muted)',
                      padding: '0.0625rem 0.375rem',
                      borderRadius: '0.25rem',
                      backgroundColor: 'var(--bg-primary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>
                      Guest
                    </span>
                  )}
                </div>
                {player.id === myPlayer?.id && (
                  <span style={{
                    fontSize: '0.625rem',
                    fontWeight: '600',
                    color: 'var(--accent-primary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    flexShrink: 0,
                  }}>
                    Owner
                  </span>
                )}
                {isOwner && player.id !== myPlayer?.id && (
                  <button
                    onClick={() => handleRemovePlayer(player.id)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '9999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--accent-danger, #ef4444)',
                      flexShrink: 0,
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Players section (owner only) */}
      {isOwner && (
        <>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-primary)', marginBottom: '0.75rem' }}>
            Add Players
          </h2>

          {/* Manual add form */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '0.75rem',
            border: '1px solid var(--border-color)',
            overflow: 'hidden',
            marginBottom: '1rem',
          }}>
            <button
              onClick={() => setShowManualForm(!showManualForm)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                width: '100%',
                padding: '0.75rem',
                fontSize: '0.8125rem',
                fontWeight: '600',
                color: 'var(--text-secondary)',
              }}
            >
              <UserPlus size={16} />
              Add Player Manually
              {showManualForm ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {showManualForm && (
              <form onSubmit={handleManualAdd} style={{ padding: '0 1rem 1rem' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    placeholder="e.g. Tiger"
                    autoFocus
                    required
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '0 0.75rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
                {manualDedupSuggestions.length > 0 && (
                  <div style={{
                    marginBottom: '0.75rem',
                    padding: '0.625rem',
                    borderRadius: '0.375rem',
                    backgroundColor: 'rgba(245, 158, 11, 0.08)',
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                  }}>
                    <p style={{ fontSize: '0.6875rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                      You already created:
                    </p>
                    {manualDedupSuggestions.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          handleAddPlayer(p.id)
                          setManualName('')
                          setManualEmail('')
                          setShowManualForm(false)
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.375rem',
                          borderRadius: '9999px',
                          marginBottom: '0.25rem',
                          textAlign: 'left',
                        }}
                      >
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: p.avatar_color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '0.625rem',
                          color: '#fff',
                          flexShrink: 0,
                        }}>
                          {getInitials(p)}
                        </div>
                        <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: '500' }}>
                          {p.display_name}
                        </span>
                        <span style={{
                          fontSize: '0.6875rem',
                          fontWeight: '600',
                          color: '#f59e0b',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '1rem',
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                          flexShrink: 0,
                        }}>
                          Add
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                    Email (optional)
                  </label>
                  <input
                    type="email"
                    value={manualEmail}
                    onChange={(e) => setManualEmail(e.target.value)}
                    placeholder="e.g. tiger@golf.com"
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '0 0.75rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.375rem', fontStyle: 'italic' }}>
                    Adding their email lets them find their scores and claim their account
                  </p>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                    GHIN Number (optional)
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={manualGhin}
                      onChange={(e) => setManualGhin(e.target.value)}
                      placeholder="e.g. 1234567"
                      style={{
                        flex: 1,
                        height: '40px',
                        padding: '0 0.75rem',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!manualGhin.trim()) return
                        ghinLookup.mutate(manualGhin.trim(), {
                          onSuccess: (data) => {
                            if (data.handicap_index != null) {
                              setManualHandicap(String(data.handicap_index))
                            }
                          },
                        })
                      }}
                      disabled={ghinLookup.isPending || !manualGhin.trim()}
                      style={{
                        height: '40px',
                        padding: '0 0.625rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#fff',
                        backgroundColor: 'var(--accent-primary)',
                        border: 'none',
                        cursor: ghinLookup.isPending || !manualGhin.trim() ? 'not-allowed' : 'pointer',
                        opacity: ghinLookup.isPending || !manualGhin.trim() ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        flexShrink: 0,
                      }}
                    >
                      <RefreshCw size={12} style={ghinLookup.isPending ? { animation: 'spin 1s linear infinite' } : {}} />
                      Lookup
                    </button>
                  </div>
                  {ghinLookup.isError && (
                    <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.375rem' }}>
                      {ghinLookup.error?.message || 'GHIN lookup failed'}
                    </p>
                  )}
                  {ghinLookup.isSuccess && (
                    <p style={{ color: 'var(--accent-primary)', fontSize: '0.75rem', marginTop: '0.375rem' }}>
                      Handicap updated from GHIN
                    </p>
                  )}
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                    Handicap Index (optional)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={manualHandicap}
                    onChange={(e) => setManualHandicap(e.target.value)}
                    placeholder="e.g. 12.4"
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '0 0.75rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
                    Color
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {AVATAR_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setManualColor(c)}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          backgroundColor: c,
                          border: manualColor === c ? '3px solid var(--text-primary)' : '3px solid transparent',
                        }}
                      />
                    ))}
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!manualName.trim() || createPlayer.isPending}
                  style={{
                    width: '100%',
                    height: '40px',
                    fontSize: '0.875rem',
                    borderRadius: '9999px',
                    opacity: (!manualName.trim() || createPlayer.isPending) ? 0.5 : 1,
                  }}
                >
                  {createPlayer.isPending ? 'Adding...' : 'Create & Add'}
                </button>
              </form>
            )}
          </div>

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem',
          }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '500' }}>or</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
          </div>

          {/* Search bar */}
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '0.875rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search players..."
              style={{
                width: '100%',
                height: '44px',
                paddingLeft: '2.75rem',
                paddingRight: searchText ? '2.5rem' : '0.875rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-primary)',
              }}
            />
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                style={{
                  position: 'absolute',
                  right: '0.625rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </>
      )}

      {/* Search results */}
      {isOwner && isSearching && (matchingFollowed.length > 0 || localResults.length > 0 || isSearching) && (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
          padding: '0.75rem',
          marginBottom: '1rem',
        }}>
          <h3 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.5rem',
          }}>
            Search Results
          </h3>
          {isSearching && matchingFollowed.length === 0 && localResults.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '0.5rem 0' }}>
              No results found
            </p>
          )}
          {[...matchingFollowed, ...localResults].map((player) => {
            const isManual = !player.user_id && player.created_by === user?.id
            const isFollowed = followedPlayerIds.has(player.id) || (myFollows || []).some((f) => f.id === player.id)
            return (
              <div
                key={player.id}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  padding: '0.625rem',
                  marginBottom: '0.25rem',
                  borderRadius: '0.375rem',
                  textAlign: 'left',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: player.avatar_color || '#0ea47a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '0.6875rem',
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  {getInitials(player)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {isManual && (
                    <div style={{
                      fontSize: '0.5625rem',
                      fontWeight: '600',
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '0.125rem',
                    }}>
                      Manually Created
                    </div>
                  )}
                  <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {player.first_name || player.last_name
                      ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
                      : player.display_name}
                  </div>
                  {player.display_name && (player.first_name || player.last_name) && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {player.display_name}
                    </div>
                  )}
                  {hasHandicapIndex(player.handicap_index) && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      HCP: {formatHandicapIndex(player.handicap_index)}
                    </div>
                  )}
                </div>
                {isFollowed && (
                  <span style={{
                    fontSize: '0.625rem',
                    fontWeight: '600',
                    color: 'var(--text-muted)',
                    padding: '0.125rem 0.375rem',
                    borderRadius: '0.25rem',
                    backgroundColor: 'var(--bg-primary)',
                    flexShrink: 0,
                  }}>
                    Following
                  </span>
                )}
                <button
                  onClick={() => handleAddPlayer(player.id)}
                  onTouchStart={() => setPressedAddId(player.id)}
                  onTouchEnd={() => setPressedAddId(null)}
                  onTouchCancel={() => setPressedAddId(null)}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'var(--accent-primary)',
                    padding: '0.25rem 0.625rem',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                    flexShrink: 0,
                    transform: pressedAddId === player.id ? 'scale(0.9)' : 'scale(1)',
                    opacity: pressedAddId === player.id ? 0.6 : 1,
                    transition: 'transform 0.15s ease, opacity 0.15s ease',
                  }}
                >
                  Add
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Spacer so content isn't hidden behind fixed Save bar */}
      {isOwner && <div style={{ height: '80px' }} />}

      {/* Fixed Save button at bottom of screen */}
      {isOwner && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: `0.75rem 1rem max(0.75rem, calc(0.25rem + env(safe-area-inset-bottom, 0px)))`,
          backgroundColor: 'var(--bg-primary)',
          borderTop: '1px solid var(--border-color)',
          zIndex: 100,
        }}>
          <button
            onClick={handleSave}
            disabled={saveMembers.isPending || !groupName.trim()}
            className="btn-primary"
            style={{
              width: '100%',
              height: '48px',
              fontSize: '1rem',
              fontWeight: '600',
              borderRadius: '9999px',
              opacity: (saveMembers.isPending || !groupName.trim()) ? 0.6 : 1,
            }}
          >
            {saveMembers.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {/* Discard / Delete confirmation modal */}
      {showDiscardConfirm && (
        <div
          onClick={() => setShowDiscardConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              padding: '1.5rem',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            }}
          >
            <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
              {isNew ? 'Discard Group?' : 'Delete Group?'}
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={handleDiscard}
                disabled={deleteGroupMut.isPending}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  borderRadius: '9999px',
                  color: '#fff',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  cursor: 'pointer',
                  opacity: deleteGroupMut.isPending ? 0.6 : 1,
                }}
              >
                {deleteGroupMut.isPending ? (isNew ? 'Discarding...' : 'Deleting...') : (isNew ? 'Discard' : 'Delete')}
              </button>
              <button
                onClick={() => setShowDiscardConfirm(false)}
                disabled={deleteGroupMut.isPending}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  borderRadius: '9999px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
